import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  getNotificationSettings,
  type NotificationSettings,
  upsertNotificationSettings,
} from "@/api/notificationSettings";
import { DEFAULT_NOTIFICATION_SETTINGS } from "@/lib/constant";
import { syncNotificationsToNative } from "@/lib/notificationBridge";
import { useAuthStore } from "@/store/authStore";

const notificationSettingsQueryKey = (userId?: string) => ["notificationSettings", userId ?? "anonymous"] as const;

/** 알림 설정 조회 (DB 값, 없으면 기본값) */
export function useNotificationSettingsQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: notificationSettingsQueryKey(userId!),
    queryFn: () => getNotificationSettings(userId!),
    enabled: Boolean(userId),
    select: data => ({ ...DEFAULT_NOTIFICATION_SETTINGS, ...data }),
  });
}

type MutationContext = { previous: unknown };

/** 알림 설정 변경 뮤테이션 (낙관적 업데이트 + 네이티브 동기화) */
export function useUpdateNotificationSettingsMutation() {
  const userId = useAuthStore(state => state.user?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<NotificationSettings>) => {
      if (!userId) {
        throw new Error("로그인이 필요합니다.");
      }

      return upsertNotificationSettings(userId, settings);
    },
    onMutate: async (settings: Partial<NotificationSettings>): Promise<MutationContext> => {
      const key = notificationSettingsQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old: NotificationSettings | null) => ({
        ...DEFAULT_NOTIFICATION_SETTINGS,
        ...old,
        ...settings,
      }));

      return { previous };
    },
    onSuccess: async (_data, settings) => {
      const key = notificationSettingsQueryKey(userId);
      const current = queryClient.getQueryData(key) as NotificationSettings | null;
      const merged = { ...DEFAULT_NOTIFICATION_SETTINGS, ...current, ...settings };

      await syncNotificationsToNative(merged);
    },
    onError: (_err, _vars, context?: MutationContext) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(notificationSettingsQueryKey(userId), context.previous);
      }

      toast.error("알림 설정 저장에 실패했어요. 잠시 후 다시 시도해 주세요.", {
        richColors: true,
        position: "top-center",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationSettingsQueryKey(userId) });
    },
  });
}
